import { FC } from "react";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { CommonStyle, ControlledForm, Loader } from "Components";
import CstmBtn from "Components/CstmBtn";
import NoData from "Components/NoData";
import { FormatOrgListType } from "DataTypes/PracticeMangement.type";
import { PracticeInsuranceRow } from "./types";
import { getKey, insuranceName } from "./Format";
import { MappingSwitch, StatusPill } from "./PracticeInsuranceCommon";
import { PrgmListResType } from "DataTypes/Services/Prgm.type";

export interface ProgramInsuranceSectionProps {
  selectedPractice: FormatOrgListType | null;
  insuranceRows: PracticeInsuranceRow[];
  programColumns: PrgmListResType[];
  selectedProgramAbbr: string;
  onSelectProgramAbbr: (abbrevation: string) => void;
  selectedProgram: PrgmListResType | undefined;
  insuranceSearch: string;
  onInsuranceSearchChange: (value: string) => void;
  selectAllChecked: boolean;
  onSelectAll: () => void;
  mappingLoading: boolean;
  saving: boolean;
  hasChanges: boolean;
  filteredInsuranceRows: PracticeInsuranceRow[];
  checkedKeys: Set<string>;
  onCheck: (insurance: PracticeInsuranceRow, program: PrgmListResType) => void;
  onSave: () => void;
}

const ProgramInsuranceSection: FC<ProgramInsuranceSectionProps> = (props) => {
  const {
    selectedPractice,
    insuranceRows,
    programColumns,
    selectedProgramAbbr,
    onSelectProgramAbbr,
    selectedProgram,
    insuranceSearch,
    onInsuranceSearchChange,
    selectAllChecked,
    onSelectAll,
    mappingLoading,
    saving,
    hasChanges,
    filteredInsuranceRows,
    checkedKeys,
    onCheck,
    onSave,
  } = props;

  return (
    <Box sx={{ ...sxContent }}>
      <Box sx={{ ...CommonStyle.sxRow, justifyContent: "space-between" }}>
        <Box ml={0.5}>
          <Box sx={{ ...CommonStyle.sxRow }}>
            <Typography variant="subtitle1" fontWeight="bold" mr={1}>
              {selectedPractice?.name || "Select Practice"}
            </Typography>
            {selectedPractice?.status && (
              <StatusPill status={selectedPractice.status} />
            )}
          </Box>
          <Typography variant="subtitle2">
            {`${insuranceRows.length} Insurances`}
          </Typography>
        </Box>
      </Box>

      <Typography variant="subtitle2" fontWeight="bold" mt={1.25} mb={0.75}>
        Programs
      </Typography>
      <Box sx={{ ...sxProgramGrid }}>
        {programColumns.map((program) => {
          const selected = selectedProgramAbbr == program.abbrevation;
          const programCount = program.id
            ? insuranceRows.filter((insurance) =>
              checkedKeys.has(getKey(insurance, program))
            ).length
            : 0;
          return (
            <Tooltip key={program.abbrevation} title={program.name} arrow>
              <Button
                sx={{
                  ...sxProgramBtn,
                  ...(selected && sxProgramBtnSelected),
                }}
                disabled={!program.id}
                onClick={() => onSelectProgramAbbr(program.abbrevation || "")}
              >
                <Box component="span">{program.abbrevation}</Box>
                <Box component="span" sx={sxProgramCount}>
                  {programCount}
                </Box>
              </Button>
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ ...sxToolbar }}>
        <Box sx={{ width: { xs: "100%", sm: 255, md: 291 } }}>
          <ControlledForm.InputApi
            id="insurance-search"
            placeholder="Search insurance"
            inputVal={insuranceSearch}
            inputChange={(val) =>
              onInsuranceSearchChange(typeof val == "string" ? val : "")
            }
            iconId="search"
          />
        </Box>
        <Box sx={{ ...sxSelectAll }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Select All
          </Typography>
          <MappingSwitch
            checked={selectAllChecked}
            disabled={
              !selectedProgram?.id ||
              saving ||
              mappingLoading ||
              filteredInsuranceRows.length == 0
            }
            onChange={onSelectAll}
          />
        </Box>
      </Box>

      <Box sx={{ ...sxInsuranceGrid }}>
        {mappingLoading ? (
          <Loader />
        ) : filteredInsuranceRows.length == 0 ? (
          <Box sx={{ ...sxNoInsuranceData }}>
            <NoData />
          </Box>
        ) : (
          filteredInsuranceRows.map((insurance) => {
            const checked = selectedProgram?.id
              ? checkedKeys.has(getKey(insurance, selectedProgram))
              : false;
            return (
              <Box key={insurance.id} sx={{ ...sxInsuranceCard }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={sxInsuranceName}
                >
                  {insuranceName(insurance)}
                </Typography>
                <MappingSwitch
                  checked={checked}
                  disabled={!selectedProgram?.id || saving}
                  onChange={() =>
                    selectedProgram && onCheck(insurance, selectedProgram)
                  }
                />
              </Box>
            );
          })
        )}
      </Box>

      <Box sx={{ ...sxFooter }}>
        <Box />
        <CstmBtn
          label={
            selectedProgram
              ? `Save ${selectedProgram.abbrevation} Mapping`
              : "Save Mapping"
          }
          onClick={onSave}
          disable={!hasChanges || mappingLoading || !selectedPractice}
          loading={saving}
        />
      </Box>
    </Box>
  );
};

export default ProgramInsuranceSection;

const sxContent = {
  ...CommonStyle.sxWhiteBox,
  p: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  minHeight: 0,
};

const sxProgramGrid = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1.25,
};

const sxProgramBtn = {
  width: 98,
  minWidth: 98,
  minHeight: 30,
  backgroundColor: "secondary.main",
  color: "common.white",
  fontWeight: "bold",
  fontSize: "0.72rem",
  justifyContent: "center",
  gap: 0.5,
  px: 0.75,
  py: 0.35,
  transition: "transform 0.15s ease, background-color 0.15s ease",
  "&:hover": {
    transform: "scale(1.08)",
    backgroundColor: "primary.main",
  },
  "&.Mui-disabled": {
    color: "text.disabled",
    backgroundColor: "custom.main",
  },
};

const sxProgramBtnSelected = {
  backgroundColor: "primary.main",
  transform: "scale(1.03)",
};

const sxProgramCount = {
  color: "secondary.main",
  backgroundColor: "common.white",
  borderRadius: "50%",
  minWidth: 16,
  height: 16,
  px: 0.5,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.62rem",
  fontWeight: "bold",
};

const sxToolbar = {
  ...CommonStyle.sxRow,
  justifyContent: "space-between",
  gap: 0.75,
  flexWrap: "wrap",
  my: 0.75,
};

const sxSelectAll = {
  ...CommonStyle.sxRow,
  border: "1px solid",
  borderColor: "custom.main",
  borderRadius: 1,
  px: 0.75,
  py: 0.15,
};

const sxInsuranceGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
  gap: 0.5,
  flex: 1,
  overflow: "auto",
  pr: 0.5,
  alignContent: "start",
};

const sxNoInsuranceData = {
  gridColumn: "1 / -1",
  minHeight: 260,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const sxInsuranceCard = {
  ...CommonStyle.sxRow,
  justifyContent: "space-between",
  alignItems: "center",
  gap: 0.75,
  border: "1px solid",
  borderColor: "custom.main",
  borderRadius: 1,
  px: 1,
  py: 0.35,
  minHeight: 34,
};

const sxInsuranceName = {
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  flex: 1,
  minWidth: 0,
  lineHeight: 1.25,
  fontSize: "0.78rem",
};

const sxFooter = {
  ...CommonStyle.sxRow,
  justifyContent: "space-between",
  borderTop: "1px solid",
  borderColor: "custom.main",
  pt: 0.75,
  mt: 0.75,
};
